import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { displayEventDate, displayEventTime } from "@/lib/datetime";
import { formatGroupTime, resolveGroupHosts, sortEventGroups } from "@/lib/eventGroups";
import { Event } from "@/types/event";
import { Calendar, Coins, ImageOff, Pencil } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { MembershipDiscount } from "@/hooks/useMembershipDiscountStatus";
import { formatPrice } from "@/lib/currency";
import EarlyBirdBadge from "@/components/event/EarlyBirdBadge";
import { useEventEarlyBird } from "@/hooks/useEventEarlyBird";
import { useCardFriendsPrice } from "@/hooks/useFriendsUpsell";

type Props = {
  event: Event;
  isBooked: boolean;
  isArchived?: boolean;
  adventPassData?: {
    hasPass: boolean;
    validFrom: Date | null;
    validUntil: Date | null;
    discountPercentage: number;
  } | null;
  adventPassEligibility?: {
    validFrom: Date | null;
    validUntil: Date | null;
    discountPercentage: number;
    appliesToTags: string[];
  } | null;
  membershipDiscount?: {
    discount: MembershipDiscount;
    label: string;
  } | null;
  isOnWaitlist?: boolean;
  onEdit?: (event: Event) => void;
};

const EventCardNew = ({ event, isBooked, isArchived = false, adventPassData, adventPassEligibility, membershipDiscount, isOnWaitlist = false, onEdit }: Props) => {
  const [imageFailed, setImageFailed] = useState(false);
  // Check if this is a Secret Salon event
  const isSecretSalon = event?.tags?.some(tag => tag.toLowerCase() === 'secret salon') || false;
  
  // Check if this is a Nimi Classics event
  const isNimiClassic = event?.tags?.some(tag => tag.toLowerCase() === 'nimi classics') || false;

  const isGalleryStroll = event?.tags?.some(tag => tag.toLowerCase() === 'gallery stroll') || false;
  const departureGroups = sortEventGroups(event.groups ?? []);
  const visibleDepartures = departureGroups.length > 2
    ? [departureGroups.find((group) => group.spots_remaining > 0) ?? departureGroups[0]]
    : departureGroups;
  
  // Check if event is ELIGIBLE for Advent Pass (based on eligibility criteria, for ALL users)
  const eventStartTime = new Date(event.start_time);
  const isEligibleForAdventPass = adventPassEligibility && isNimiClassic && (
    (!adventPassEligibility.validFrom || eventStartTime >= adventPassEligibility.validFrom) &&
    (!adventPassEligibility.validUntil || eventStartTime <= adventPassEligibility.validUntil)
  );
  
  // Check if user HAS ACTIVE PASS and event qualifies (for showing discounted prices)
  const userHasActivePass = adventPassData?.hasPass || false;
  const showAdventDiscount = userHasActivePass && isEligibleForAdventPass;
  
  // Early bird (server-authoritative)
  const { data: eb } = useEventEarlyBird(event.id);
  const ebActive = !!eb?.active;

  // Determine the best discount to show (Advent Pass or Membership)
  const adventDiscountPct = showAdventDiscount && adventPassEligibility ? adventPassEligibility.discountPercentage : 0;
  const memberDiscountPct = membershipDiscount ? membershipDiscount.discount.discount_percentage : 0;

  // Pick the higher discount (member/advent tier)
  const hasMemberDiscount = showAdventDiscount || !!membershipDiscount;
  const bestDiscountPct = Math.max(adventDiscountPct, memberDiscountPct);
  const discountLabel = memberDiscountPct >= adventDiscountPct && membershipDiscount
    ? membershipDiscount.label
    : "🎁 Advent Pass";

  // Price waterfall (mirrors server order: EB → member/advent %)
  const originalPrice = event.price;
  const ebPrice = ebActive && eb?.discounted_price != null ? Number(eb.discounted_price) : originalPrice;
  const finalPrice = hasMemberDiscount ? ebPrice * (1 - bestDiscountPct / 100) : ebPrice;
  const showPriceStrikethrough = ebActive || hasMemberDiscount;

  // Non-members: show what Nimi Friends members pay beside today's price.
  const friendsPrice = useCardFriendsPrice(event, ebPrice);

  // Credits waterfall
  const originalCredits = event.credit_cost;
  const ebCredits = ebActive && eb?.discounted_credit_cost != null
    ? Number(eb.discounted_credit_cost)
    : originalCredits;
  const finalCredits = hasMemberDiscount && ebCredits
    ? Math.max(1, Math.ceil(ebCredits * (1 - bestDiscountPct / 100)))
    : ebCredits;
  const showCreditsStrikethrough = !!originalCredits && !!finalCredits && finalCredits < originalCredits;
  
  // Check if event is credits-only (no Stripe payment allowed)
  const isCreditsOnly = event.allowed_booking_methods?.length === 1 && 
    event.allowed_booking_methods.includes('credits');
  
  // Determine button text with priority
  const isInProgress = !isArchived && new Date(event.start_time).getTime() <= Date.now();
  let buttonText = "Book";
  if (isInProgress) {
    buttonText = "In progress";
  } else if (isArchived) {
    buttonText = "View";
  } else if (isBooked) {
    buttonText = "Details";
  } else if (isOnWaitlist && isSecretSalon) {
    buttonText = "Pending";
  } else if (isSecretSalon) {
    buttonText = "Request invite";
  }
  
  // Status pill is rendered only when there's a meaningful user-facing state.
  // Regular unbooked events show no button — the whole card is clickable.
  const showStatusPill = isInProgress || isArchived || isBooked || isSecretSalon;

  // Update background color for waitlist
  const backgroundClass = isBooked
    ? "bg-success/10"
    : isOnWaitlist
    ? "bg-warning/10"
    : "bg-card";
  
  return (
    <Link to={`/events/${event.id}`} className="group mb-4 block w-full min-w-0 max-w-full">
      <div
        className={`relative flex min-h-44 w-full min-w-0 max-w-full overflow-hidden rounded-lg shadow-md transition-shadow hover:shadow-lg ${backgroundClass} ${isInProgress ? "opacity-60 grayscale" : ""}`}
      >
        <div className="relative w-28 shrink-0 overflow-hidden bg-muted sm:w-40">
          {event.image_url && !imageFailed ? (
            <img
              src={event.image_url}
              alt=""
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-[1.03]"
              loading="lazy"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground" aria-hidden="true">
              <ImageOff className="h-7 w-7 opacity-50" />
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col p-4 sm:p-5">
        {onEdit && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-2 top-2 z-10 h-8 w-8 bg-background/90 opacity-100 shadow-sm transition-opacity hover:bg-background sm:opacity-0 sm:group-hover:opacity-100 sm:focus-visible:opacity-100"
            aria-label={`Edit ${event.title}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit(event);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="line-clamp-2 text-sm font-semibold leading-snug sm:text-base">{event.title}</h3>
            </div>
            {!isGalleryStroll && <p className="text-xs text-muted-foreground">{event.venue}</p>}
            <p className="flex items-center gap-1.5 text-xs font-medium text-foreground">
              <Calendar className="h-3.5 w-3.5 shrink-0" />
              <span className="whitespace-nowrap leading-none">
                {departureGroups.length > 0 ? displayEventDate(event.start_time, event.timezone) : displayEventTime(event.start_time, event.timezone)}
              </span>
            </p>
            {departureGroups.length > 0 && (
              <div className="space-y-1.5 pt-1 text-xs text-foreground">
                {visibleDepartures.map((group) => {
                  const hosts = resolveGroupHosts(group, {
                    primary: event.profiles ? { ...event.profiles, id: event.host_id ?? "event-host" } : null,
                    secondary: event.secondary_profiles ? { ...event.secondary_profiles, id: event.secondary_host_id ?? "event-cohost" } : null,
                  });
                  const names = hosts.map((h) => h.first_name).filter(Boolean).join(" & ");
                  const isFull = group.spots_remaining <= 0;
                  return (
                    <div key={group.group_id} className={`flex min-w-0 items-center gap-1.5 ${isFull ? "opacity-60" : ""}`}>
                      <span className="shrink-0 font-semibold">{formatGroupTime(group.start_time, event.timezone)}</span>
                      {hosts.length > 0 && (
                        <span className="flex shrink-0 -space-x-1.5">
                          {hosts.map((h) => (
                            <Avatar key={h.id} className="h-5 w-5 ring-1 ring-background">
                              {h.avatar_url && <AvatarImage src={h.avatar_url} alt="" />}
                              <AvatarFallback className="bg-muted text-[9px] text-muted-foreground">{(h.first_name?.[0] || "?").toUpperCase()}</AvatarFallback>
                            </Avatar>
                          ))}
                        </span>
                      )}
                      {names && <span className="min-w-0 truncate text-muted-foreground">{names}</span>}
                      <span className={`shrink-0 font-medium ${isFull ? "text-muted-foreground" : "text-success"}`}>
                        · {isFull ? "Full" : "Available"}
                      </span>
                    </div>
                  );
                })}
                {departureGroups.length > 2 && (
                  <p className="text-muted-foreground">+ {departureGroups.length - 1} other departures</p>
                )}
              </div>
            )}
          </div>
          {showStatusPill && (
            <span
              className={`shrink-0 self-start rounded-full px-2.5 py-1 text-xs sm:px-3 ${
                isInProgress || isArchived
                  ? "bg-muted text-muted-foreground"
                  : isBooked
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground"
              }`}
            >
              {buttonText}
            </span>
          )}
        </div>

        {(!isBooked || event.profiles) && (
          <div className="mt-auto flex flex-col gap-2.5 pt-3">
            <div className="space-y-2 min-w-0">
              {!isBooked && (
                <>
                  {/* Discount Badge */}
                  {isEligibleForAdventPass && !membershipDiscount && (
                    <Badge variant="secondary" className="flex items-center gap-1 w-fit bg-success/20 text-success">
                      🎁 Advent Pass
                    </Badge>
                  )}
                  {membershipDiscount && (
                    <Badge variant="secondary" className="flex items-center gap-1 w-fit bg-success/20 text-success">
                      {membershipDiscount.label}
                    </Badge>
                  )}
                  {ebActive && <EarlyBirdBadge eventId={event.id} showCountdown={false} />}

                  {/* Price: member vs non-member (credits only shown on credits-only events) */}
                   <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm">
                    {!isCreditsOnly && (
                      friendsPrice && !hasMemberDiscount ? (
                        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                          <span className="font-bold text-foreground">
                            {formatPrice(friendsPrice.memberPrice, event.currency, { compact: true })} member
                          </span>
                          <span className="text-muted-foreground/50" aria-hidden>·</span>
                          <span className="font-medium text-muted-foreground">
                            {ebActive
                              ? `${formatPrice(ebPrice, event.currency, { compact: true })} early bird`
                              : `${formatPrice(ebPrice, event.currency, { compact: true })} non-member`}
                          </span>
                        </span>
                      ) : showPriceStrikethrough ? (
                        <span className="flex items-center gap-2">
                          <span className="font-medium line-through opacity-60">
                            {formatPrice(hasMemberDiscount ? ebPrice : originalPrice, event.currency, { compact: true })}
                          </span>
                          <span className="font-bold text-foreground">
                            {finalPrice <= 0 ? "Free" : formatPrice(finalPrice, event.currency, { compact: true })}
                          </span>
                        </span>
                      ) : (
                        <span className="font-bold text-foreground">{formatPrice(originalPrice, event.currency, { compact: true })}</span>
                      )
                    )}

                    {isCreditsOnly && finalCredits != null && finalCredits > 0 && (
                      <span className="flex items-center gap-1 whitespace-nowrap font-medium text-muted-foreground">
                        <Coins className="w-3 h-3" />
                        {showCreditsStrikethrough ? (
                          <>
                            <span className="line-through opacity-60">{originalCredits}</span>
                            <span>{finalCredits} credits</span>
                          </>
                        ) : (
                          <>{finalCredits} credits</>
                        )}
                      </span>
                    )}
                  </div>


                </>
              )}
            </div>

            {/* Host(s) — left-aligned below the price */}
            {departureGroups.length === 0 && event.profiles && (event.profiles.first_name || event.profiles.avatar_url) && (
              <div className="flex items-center gap-2">
                <p className="flex min-w-0 items-center gap-1.5 whitespace-nowrap text-xs leading-none text-muted-foreground">
                  <span className="font-bold uppercase tracking-wider">Host</span>
                  <span className="truncate font-semibold text-foreground">
                    {event.profiles.first_name}
                    {event.secondary_profiles && ` & ${event.secondary_profiles.first_name}`}
                  </span>
                </p>
                <div className="flex gap-1.5">
                  <Avatar className="h-8 w-8 ring-2 ring-background shadow-sm transition-all group-hover:ring-primary/40">
                    {event.profiles.avatar_url && (
                      <AvatarImage src={event.profiles.avatar_url} alt={event.profiles.first_name || "Host"} />
                    )}
                    <AvatarFallback className="bg-muted text-muted-foreground text-xs font-semibold">
                      {(event.profiles.first_name?.[0] || "?").toUpperCase()}
                      {(event.profiles.last_name?.[0] || "").toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  {event.secondary_profiles && (
                    <Avatar className="h-8 w-8 ring-2 ring-background shadow-sm transition-all group-hover:ring-primary/40">
                      {event.secondary_profiles.avatar_url && (
                        <AvatarImage
                          src={event.secondary_profiles.avatar_url}
                          alt={event.secondary_profiles.first_name || "Co-host"}
                        />
                      )}
                      <AvatarFallback className="bg-muted text-muted-foreground text-xs font-semibold">
                        {(event.secondary_profiles.first_name?.[0] || "?").toUpperCase()}
                        {(event.secondary_profiles.last_name?.[0] || "").toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </Link>
  );
};

export default EventCardNew;
